import type { FunctionComponent } from 'react';
import { memo, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import crownLogo from '../../../../assets/logo/crab-crowned.svg';
import xLogo from '../../../../assets/logo/cross.svg';
import logo from '../../../../assets/logo/logo--full-vertical.svg';
import supportLogo from '../../../../assets/logo/support.svg';
import { isInApp } from '../../../main/util/is-app';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { ImageLogo } from '../../../shared/components/image-logo/image-logo.';
import { ModalContainer } from '../../../shared/components/modal-container/modal-container';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { exists } from '../../../shared/utils/filters';
import { FooterSections } from '../../footer.interface';
import type { IFooterCollection } from '../../queries/footer.graphql';
import { FooterSection } from '../footer-section/footer-section';

import styles from './footer.module.scss';

const style = createStyleHelper(styles, 'footer');

export interface IFooterProps {
    className?: string | undefined;
    footerData: IFooterCollection[];
    currentCurrency?: string;
    currencies?: string[];
    onCurrencyChange?: (currency: string) => void;
}

export const Footer: FunctionComponent<IFooterProps> = memo(
    ({ className, footerData }) => {


        const auth = useAuth();
        const isAuthenticated = auth.isAuthenticated;
        const isNativeApp = isInApp();
        const [isOpen, setIsOpen] = useState(false);
        const openPopup = () => {
            const url = 'https://crabsports.ladesk.com/scripts/inline_chat.php?cwid=1l7jwkkr';
            const popupWindow = window.open(url, '_blank', 'width=600,height=400');
            if (popupWindow) {
                popupWindow.focus();
            }
        };
        const onClose = () => {
            setIsOpen(!isOpen);
        };
        return (
            <footer className={createClassName([className, style()])}>
                <div className={style('container')}>
                    <div className={style('section')}>
                        <ImageLogo className={style('logo')} srcPath={logo} />
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.terms"
                                defaultMessage="This website is operated by Crab Sports Inc., 900 East Fayette Street, Unit 2393 Baltimore, Maryland 21203."
                            />
                        </p>
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.terms.cookies"
                                defaultMessage="We use cookies and third-party cookies. For further information please read our Privacy Policy. Visit crabsports.com for Terms and Conditions."
                            />
                        </p>
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.terms.company"
                                defaultMessage="© 2023 Crab Sports"
                            />
                        </p>
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.terms.mrhighline"
                                defaultMessage="Designed and Developed by Mr.Highline"
                            />
                        </p>
                    </div>
                    <div className={style('section')}>
                        <FooterSection
                            className={style('links')}
                            displaySection={FooterSections.LINKS}
                            footerItems={footerData?.filter(exists) ?? []}
                         />
                    </div>

                    <div className={style('section')}>
                        <h1 className={style('label')}>
                            <FormattedMessage id="footer.label-contact" defaultMessage="CONTACT" />
                        </h1>
                        {/*<h2 className="alt" onClick={handleChatButtonClick}>*/}
                        {/*    Start chat*/}
                        {/*</h2>*/}
                        <a
                            href={'mailto:support@crabsports.com'}
                            className={style('terms')}
                            rel="noreferrer"
                        >
                            <FormattedMessage
                                id="footer.email"
                                defaultMessage="support@crabsports.com"
                            />
                        </a>
                        <a
                            href={'https://crabsports.ladesk.com/'}
                            target={'_blank'}
                            className={style('terms')}
                            rel="noreferrer"
                        >
                            <FormattedMessage id="footer.faq" defaultMessage="FAQ" />
                        </a>
                        <a href={'#'} onClick={onClose} className={style('terms')} rel="noreferrer">
                            <FormattedMessage id="footer.vip" defaultMessage="VIP PROGRAM" />
                        </a>

                        {/*<a href="#" onClick={openPopup} className={style('terms')} rel="noreferrer">*/}
                        {/*    <FormattedMessage id="footer.live-chat" defaultMessage="Live Chat" />*/}
                        {/*</a>*/}
                        {isNativeApp ? (
                            <>
                                <a
                                    href={'https://www.crabsports.com/en-US/promo'}
                                    className={style('terms')}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <FormattedMessage
                                        id="footer.promo"
                                        defaultMessage="Promotions"
                                    />
                                </a>
                                <a
                                    href={'https://www.crabsports.com/en-US/merchandise'}
                                    className={style('terms')}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <FormattedMessage id="footer.merch" defaultMessage="Merch" />
                                </a>
                                <a
                                    href={'https://www.crabsports.com/en-US/crab-club'}
                                    className={style('terms')}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <FormattedMessage id="footer.club" defaultMessage="Crab Club" />
                                </a>
                                <a
                                    href={'https://www.crabsports.com/en-US/pickem'}
                                    className={style('terms')}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <FormattedMessage
                                        id="footer.club"
                                        defaultMessage="Free Pickem"
                                    />
                                </a>
                            </>
                        ) : null}

                        {isAuthenticated ? (
                            <Button
                                className={style('orange')}
                                size={ButtonSize.MEDIUM}
                                hollow
                                light
                                onClick={openPopup}
                            >
                                <div className={style('loader')}>
                                    <ImageLogo
                                        className={style('supp-logo')}
                                        srcPath={supportLogo}
                                    />
                                </div>
                                <FormattedMessage id="footer.live-chat" defaultMessage="SUPPORT" />
                            </Button>
                        ) : (
                            <></>
                        )}
                    </div>

                    {isNativeApp ? null : (
                        <div className={style('section')}>
                            <h1 className={style('label')}>
                                <FormattedMessage
                                    id="footer.label-contact"
                                    defaultMessage="MOBILE APP"
                                />
                            </h1>
                            <FooterSection
                                className={style('section-icons')}
                                displaySection={FooterSections.ICONS}
                                footerItems={footerData?.filter(exists) ?? []}
                             />
                        </div>
                    )}

                    <div className={style('section')}>
                        <h1 className={style('label')}>
                            <FormattedMessage
                                id="footer.label-disclaimer"
                                defaultMessage="DISCLAIMERS"
                            />
                        </h1>
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.disclaimer"
                                defaultMessage="Must be at least 21 years old and physically located in MD to wager. Please play responsibly, for help visit mdgamblinghelp.org or call 1-800-GAMBLER"
                            />
                        </p>
                        <h1 className={style('label')}>
                            <FormattedMessage id="footer.label-license" defaultMessage="LICENSE" />
                        </h1>
                        <p className={style('terms')}>
                            <FormattedMessage
                                id="footer.license"
                                defaultMessage="Licensed and regulated by Maryland Lottery and Gaming Control Agency"
                            />
                        </p>
                        <p className={style('version')}>
                            <FormattedMessage
                                id={'footer.label.version'}
                                defaultMessage="v:1.1.4"
                            />
                        </p>
                    </div>
                </div>
                {isOpen ? (
                    <ModalContainer onClose={onClose}>
                        <section className={style('modal')}>
                            <ImageLogo className={style('crown-logo')} srcPath={crownLogo} />
                            <div className={style('vip-title')}>
                                <h1 className={style('vip-header')}>VIP PROGRAM</h1>
                                <div onClick={onClose}>
                                    <ImageLogo className={style('x-logo')} srcPath={xLogo} />
                                </div>
                            </div>
                            <p className={style('description')}>
                                <FormattedMessage
                                    id={'vip.text'}
                                    defaultMessage={
                                        'Crab Sports is building the best VIP program for Maryland bettors. Whether you’re into free bets and bonuses to add to your bankroll or tickets to the Orioles and the Ravens, we have you covered. If you think you have what it takes to be a Crab Sports VIP email us at clientservices@crabsports.com and we’ll take it from there!'
                                    }
                                />

                                <a
                                    href={'mailto:clientservices@crabsports.com'}
                                    className={style('description')}
                                >
                                    <FormattedMessage
                                        id="vip.link"
                                        defaultMessage=" clientservices@crabsports.com"
                                    />
                                </a>
                                <FormattedMessage
                                    id={'vip.text2'}
                                    defaultMessage={' and we’ll take it from there!'}
                                />
                            </p>
                        </section>
                    </ModalContainer>
                ) : null}
            </footer>
        );
    },
);
